import { BoundingInfo } from '@babylonjs/core/Culling/boundingInfo';
import { Engine } from '@babylonjs/core/Engines/engine';
import '@babylonjs/core/Helpers/sceneHelpers';
import '@babylonjs/core/Layers/effectLayerSceneComponent'; // Required by highlight layer
import { Light, PointLight, SpotLight } from '@babylonjs/core/Lights';
import { Color3, Color4, Quaternion, Vector3 } from '@babylonjs/core/Maths';
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { VertexBuffer } from '@babylonjs/core/Meshes/buffer';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import {
    HardwareScalingOptimization,
    LensFlaresOptimization,
    ParticlesOptimization,
    PostProcessesOptimization,
    RenderTargetsOptimization,
    SceneOptimizer,
    SceneOptimizerOptions,
    ShadowsOptimization,
    TextureOptimization,
} from '@babylonjs/core/Misc/sceneOptimizer';
import { SceneSerializer } from '@babylonjs/core/Misc/sceneSerializer';
import '@babylonjs/core/Rendering/depthRendererSceneComponent'; // For SSAO Pipeline
import '@babylonjs/core/Rendering/edgesRenderer'; // Required by mesh renderOverlay
import '@babylonjs/core/Rendering/geometryBufferRendererSceneComponent'; // For SSRPrepass and LensPipeline
import '@babylonjs/core/Rendering/outlineRenderer'; // Required by mesh renderOverlay
import '@babylonjs/core/Rendering/prePassRendererSceneComponent'; // For SSRPrepass and LensPipeline
import { UtilityLayerRenderer } from '@babylonjs/core/Rendering/utilityLayerRenderer';
import { Scene } from '@babylonjs/core/scene';
import { DeepImmutableObject } from '@babylonjs/core/types';
// import "@babylonjs/loaders/glTF";
// import '@babylonjs/loaders/glTF/2.0';
// import "@babylonjs/loaders/OBJ";
import { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
import { BaseTexture } from '@babylonjs/core/Materials/Textures/baseTexture';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { Material } from '@babylonjs/core/Materials/material';
import { MultiMaterial } from '@babylonjs/core/Materials/multiMaterial';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import {
    BaseConnector,
    Feature,
    IVertexGroup,
    KbQuaternion,
    MateNode,
    MeshGroupNode,
    SketchNode,
} from '@kb3d/models/classes';
import { nextTick } from '@kb3d/models/classes/decorators/change-tracker';
import { eLoadingExperience, eRenderMode, eViewpointTargetMode, eViewpointType } from '@kb3d/models/classes/enums';
import { isConnector } from '@kb3d/models/classes/features/connector';
import { Kb3dManager } from '@kb3d/models/classes/kb3d-manager';
import { Kb3dObject } from '@kb3d/models/classes/kb3d-object';
import { LightNode, isLightNode } from '@kb3d/models/classes/lights/light-node';
import { MaterialNode, TextureLayer, isMaterialNode, isTextureLayer } from '@kb3d/models/classes/materials';
import { MeshNode } from '@kb3d/models/classes/meshes/mesh-node';
import { isSketchControlPoint, isSpaceNode } from '@kb3d/models/classes/meshes/mesh-utilities';
import { SpaceNode } from '@kb3d/models/classes/meshes/space-node';
import { KbColor } from '@kb3d/models/classes/primitives/kb-color';
import { KbGeometry } from '@kb3d/models/classes/primitives/kb-geometry';
import { KbVector } from '@kb3d/models/classes/primitives/kb-vector';
import { SceneNode, isSceneNode } from '@kb3d/models/classes/scene-node';
import { Token } from '@kb3d/models/classes/token';
import { Validator } from '@kb3d/models/classes/validator';
import { ViewpointNode } from '@kb3d/models/classes/viewpoints';
import { KBMAXPREFIX } from '@kb3d/models/constants';
import { SettingsManagerService } from '@kb3d/models/services/settings-manager-service';
import { SyncService } from '@kb3d/models/services/sync-service';
import { Observable, Subject, firstValueFrom } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, finalize, take, takeUntil, tap, throttle, throttleTime } from 'rxjs/operators';
import { GuiHelper } from './helpers/gui-helper';
import './helpers/imagedata-polyfill';
import { getScreenBoundingBox, nodeToScreen } from './helpers/scene-helper';
import { Kb3dWorker } from './kb-worker';
import { SketchEditor, SnapshotModule } from './modules';
import { AnimationModule } from './modules/animation';
import { AR } from './modules/ar';
import { BoundingBoxCache } from './modules/bounding-box-cache';
import { CameraTools } from './modules/camera-tools';
import { GLTFKb3dExporter } from './modules/exporter-gltf';
import { ObjKb3dExporter } from './modules/exporter-obj';
import { GizmoTools } from './modules/gizmo-tools';
import { Highlighter } from './modules/highlighter';
import { Interaction } from './modules/interaction';
import { RightClickMenu } from './modules/right-click-menu';
import { SvgConverter } from './modules/sketching/svg-converter';
import { RendererManager } from './render/renderer-manager';

interface IKbViewerOptions {
    /** HTML Element to insert the viewer canvas into. Can be any block-level element. */
    container: HTMLElement;
    debugMode?: boolean;
    highlightColor?: KbColor;
    editorMode?: boolean;
    enableScreenshots?: boolean;
    forceDisablePostProcess?: boolean;
    forceDisableGraphicsOptimization?: boolean;
    /** Low resolution rendering mode that triggers one full frame */
    startInLowResolution?: boolean;
    /** optionally provide a path to the kb3d webworker script. Defaults to worker3d.js */
    webworkerPath?: string;
    /** optionally provide a validator to validate nodes.  Only used by creator */
    validator?: Validator;
}
interface ILoadSceneOptions {
    sceneNode: SceneNode;
    /** overrides the default viewpoint of the scene */
    viewpoint?: ViewpointNode;
    /** overrides the frame option for the default viewpoint */
    frameViewpoint?: boolean;
    /** overrides the loading experience of the scene */
    loadingExperience?: eLoadingExperience;
    /** If provided, will defer the end of the loading experience until the promise resolves */
    deferLoadingPromise?: Promise<any>;
}

export interface IMeshSelectedEvent {
    node: Kb3dObject;
    multiSelect: boolean;
}

export interface IAfterRenderArgs {
    /** Whether the render was skipped because nothing had changed from the last render */
    skipped: boolean;
}

const FADE_TIME = 300;
const PRERENDER_SCALING_LEVEL = 50;

/**
 * Applcation class for the viewer, to contain all of the initializers and hooks and connect the modules and 3d engine.
 * @param canvasElement An HTML DOM <canvas> element that the viewer will be attached to
 */
export class KbViewer {
    options: IKbViewerOptions;

    constructor(options: IKbViewerOptions) {
        this.options = {
            highlightColor: KbColor.KbmaxOrange(),
            editorMode: false,
            forceDisablePostProcess: false,
            enableScreenshots: false,
            debugMode: false,
            webworkerPath: '/worker3d.js',
            startInLowResolution: false,
            ...options,
        };

        if (typeof window !== 'undefined' && options.webworkerPath && !options.webworkerPath.startsWith('http')) {
            this.options.webworkerPath = `${window.location.protocol}//${window.location.host}${this.options.webworkerPath}`;
        }

        Kb3dWorker.workerPath = this.options.webworkerPath!;

        // Create canvas and engine.
        this.container = options.container;
        this.canvas = this.createCanvas();
        // Anti-aliasing=true (second parameter) causes the canvas to not get cleared on iOS 15.4. Warrants testing in future iOS updates.
        const iOS = /(iPad|iPhone|iPod)/g.test(navigator.userAgent);
        this.engine = new Engine(
            this.canvas,
            !iOS,
            { stencil: true, preserveDrawingBuffer: this.options.enableScreenshots },
            true
        ); //set adaptToDeviceRatio to true, and let the hardware scaling optimizer take over from there
        this.engine.setAlphaMode(Engine.ALPHA_COMBINE);
        if (this.options.startInLowResolution) {
            this.engine.setHardwareScalingLevel(PRERENDER_SCALING_LEVEL);
        }
        this.glInfo = this.engine.getGlInfo();
        // eslint-disable-next-line no-console
        console.log('WebGL Renderer', this.glInfo.vendor, this.glInfo.renderer, this.glInfo.version);

        // Create the scene.
        const scene = this.createScene();
        this.scene = scene;
        this.scene.hoverCursor = 'pointer';
        this.utilityLayer = new UtilityLayerRenderer(this.scene);
        this.utilityLayer.utilityLayerScene.useRightHandedSystem = true;
        this.utilityLayer.processAllEvents = true;

        this.camera = new CameraTools(this.scene, this.canvas, this);
        this.bbCache = new BoundingBoxCache(this);

        // var hdrTexture = CubeTexture.CreateFromPrefilteredData('/assets/environments/environment.dds', scene);
        // hdrTexture.gammaSpace = false;
        // scene.environmentTexture = hdrTexture;
        this.highlightColor = options.highlightColor || KbColor.KbmaxOrange();

        this.gizmos = new GizmoTools(scene, this);
        this.rightClickMenu = new RightClickMenu(this, this.scene);
        this.animation = new AnimationModule(this, this.scene);
        this.highlighter = new Highlighter(this, this.scene, this.highlightColor, this.camera);
        this.interaction = new Interaction(this, this.scene, this.highlighter);
        this.sketchEditor = new SketchEditor(this, this.scene, this.highlightColor);
        this.gui = new GuiHelper(scene, this.highlighter);
        this.ar = new AR(this, scene);
        this.snapshot = new SnapshotModule(this, scene);
        this.rendererManager = new RendererManager(this, scene);

        this.interaction.attachScene(this.utilityLayer.utilityLayerScene, true);
        this.gltfExporter = new GLTFKb3dExporter(scene);
        this.objExporter = new ObjKb3dExporter(scene, this.rendererManager);

        // Uncomment to add PrintScreen key listener for explicit frame rendering
        // this.setupPrintScreenListener();
    }

    /** Public Properties */
    public scene: Scene;
    public pauseRender = false;
    public canvas?: HTMLCanvasElement & Element;
    public uiOverlay?: HTMLDivElement;
    //public contextmenu?: HTMLDivElement;
    public container: HTMLElement;
    public sceneNode: SceneNode;
    public renderMode: eRenderMode.shaded;
    public warningMessages: string[];
    public lightsWarningMessages: string[];
    public textureWarningMessages: string[];
    public MeshWarningTracker = new Map<
        string,
        {
            name: string;
            isEnabled: boolean;
            castShadows: boolean;
            receiveShadows: boolean;
            polygonNumber: number;
            featureNumber: number;
        }
    >();
    public meshWarningMessages: string[];
    public materialWarningMessages: string[];
    public featureWarningMessages = new Map<string, string>();
    public totalPolygons: number;

    /** Events / Observables */
    /** observable for tracking when something changed that could affect the position of a node on the screen */
    public viewChanged = new Subject<void>();
    public afterRender = new Subject<IAfterRenderArgs>();
    public fpsTracker = new Subject<string>();
    public polygonTracker = new Subject<void>();
    public warningTracker = new Subject<void>();
    public meshWarningsNeedsUpdate = new Subject<void>();
    public materialWarningsNeedsUpdate = new Subject<void>();
    public animationWarningsNeedsUpdate = new Subject<number>();

    /** Modules */
    public rendererManager: RendererManager;
    public readonly interaction: Interaction;
    public gizmos: GizmoTools;
    public rightClickMenu: RightClickMenu;
    public camera: CameraTools;
    public gui: GuiHelper;
    public sketchEditor: SketchEditor;
    public svgConverter: SvgConverter;
    public bbCache: BoundingBoxCache;
    public readonly animation: AnimationModule;
    public readonly highlighter: Highlighter;
    public readonly ar: AR;
    public readonly snapshot: SnapshotModule;
    public readonly utilityLayer: UtilityLayerRenderer;
    public readonly glInfo: {
        vendor: string;
        renderer: string;
        version: string;
    };
    public highlightColor: KbColor;

    private loadingOverlay?: HTMLDivElement;
    private processingIndicator?: HTMLDivElement;
    private disposed = new Subject<void>();
    private isDisposed = false;
    /** @internal
     * Forces the renderer to draw to screen every browser frame, disables the 1/10 fps limiter
     */
    private forceRunRenderer = false;
    /** @internal
     * Freezes the frame to full resolution
     */
    private freezeFrame = false;
    /** @internal
     * Forces the renderer to draw to screen on the next browser frame
     */
    private forceRenderOnNextFrame = false;
    private engine: Engine;
    private manager: Kb3dManager;
    private gltfExporter: GLTFKb3dExporter;
    private objExporter: ObjKb3dExporter;
    private isDevEnvironment = false;
    /** tracks babylon nodes who are being tracked for positions (hotspots) */
    private positionTrackedNodes = new Map<string, TransformNode | undefined>();

    private nextFrameCallbacks: Array<() => void> = [];
    private resizeObserver?: ResizeObserver;
    private _renderLoopRunning = false;
    private printScreenListener?: (event: KeyboardEvent) => void;

    /** tracks any additional promises that renders and load spinners should wait for */
    private additionalLoadBlockers: Array<Promise<any>> = [];

    get settings() {
        return SettingsManagerService;
    }

    setRenderMode(mode: eRenderMode, force?: boolean) {
        this.rendererManager.setRenderMode(mode, this.sceneNode, force);
    }

    loadScene(o: ILoadSceneOptions) {
        if (!o.sceneNode._manager) {
            throw 'sceneNode must be constructed with a manager';
        }

        this.sceneNode = o.sceneNode;
        this.manager = o.sceneNode._manager as Kb3dManager;
        this.isDevEnvironment = this.manager.isDevEnvironment;

        const loadingExperience = o.loadingExperience || this.sceneNode.loadingExperience;
        this.createLoadingExperience(loadingExperience, this.sceneNode.loadingImagePath);

        let viewpoint =
            o.viewpoint ||
            (o.sceneNode.viewpointId ? this.manager.getById<ViewpointNode>(o.sceneNode.viewpointId) : undefined);
        if (!viewpoint) {
            viewpoint = this.manager.create(Token.ViewpointNode);
            viewpoint.type = eViewpointType.orbit;
            viewpoint.targetMode = eViewpointTargetMode.point;
            viewpoint.position = new KbVector(3, 3, 3);
            viewpoint.targetPoint = KbVector.Zero();
        }

        if (this.options.editorMode) {
            viewpoint = this.manager.create(Token.ViewpointNode);
            viewpoint.type = eViewpointType.orbit;
            viewpoint.targetMode = eViewpointTargetMode.point;
            viewpoint.position = new KbVector(3, 3, 3);
            viewpoint.targetPoint = KbVector.Zero();
        }

        this.camera.moveCameraToViewpoint(viewpoint);       

        // Start render loop.
        this.startRender();

        // The canvas/window resize event handler.
        // window.addEventListener('resize', () => {
        //     if (!this.ar.xrLoading && !this.ar.xrActive) { //don't mess with the AR canvas resize
        //         this.engine.resize();
        //         this.viewChanged.next();
        //     }
        // });

        const promises = [this.whenAssetsLoaded()];
        if (o.deferLoadingPromise) promises.push(o.deferLoadingPromise);

        return Promise.all(promises).then(() => {
            const http = this.sceneNode._manager?.getService(Token.HttpService);

            let frame = true;
            if (this.sceneNode.viewpointId) frame = this.sceneNode.frameViewpoint;
            if (o.frameViewpoint != null) frame = o.frameViewpoint;

            if (frame) {
                this.camera.frameCamera(this.sceneNode);
            }
            this.hideLoadingExperience();
            this.rendererManager.featuresWorker.busy
                .pipe(
                    takeUntil(this.disposed),
                    distinctUntilChanged(),
                    tap(busy => {
                        if (!busy) {
                            // If the worker just finished, we have new geometry to show, so force a render
                            this.forceAndWaitForNextRender();
                        }
                    }),
                    debounceTime(250) // Only show the spinner if the worker is busy for more than 250ms
                )
                .subscribe(busy => {
                    if (!this.processingIndicator) {
                        return;
                    }

                    this.processingIndicator.style.display = busy ? 'block' : 'none';
                });

            // Wait for everything to be loaded and processed before trying to optimize scene because the scene optimizer watches the FPS and we want the state to be stable
            // Disable the graphics optimization (for example when rendering)
            if (!this.options.forceDisableGraphicsOptimization) {
                this.waitForNextKb3dRender().then(() => {
                    this.optimizeScene(this.scene);
                });
            }

            this.meshWarningsNeedsUpdate
                .pipe(throttleTime(1000, undefined, { leading: true, trailing: true }))
                .subscribe(() => {
                    this.updateMeshWarnings();
                    this.updateTextureWarnings();
                });

            this.materialWarningsNeedsUpdate
                .pipe(throttleTime(15000, undefined, { leading: true, trailing: false }))
                .subscribe(() => {
                    this.updateMaterialWarnings();
                });

            this.animationWarningsNeedsUpdate.subscribe(value => {
                this.updateAnimationWarnings(value);
            });
            this.updateTextureWarnings();
            this.updateLightWarnings();
            this.updateMaterialWarnings();
            if (http) {
                http.executeDeferredCalls();
            }
        });
    }

    getNode<T extends Kb3dObject>(id: string) {
        return this.manager.getById<T>(id);
    }

    getRendererNode(n: LightNode): Light;
    getRendererNode(n: SpaceNode): TransformNode | AbstractMesh;
    getRendererNode(n: BaseConnector): TransformNode;
    getRendererNode(n: MaterialNode): PBRMaterial;
    getRendererNode(n: TextureLayer): Texture;
    getRendererNode(n: Kb3dObject | TextureLayer) {
        if (isSceneNode(n) && n.isNested() === false) {
            return this.rendererManager.sceneRenderer.get3d(n);
        } else if (isSceneNode(n) && n.isNested()) {
            return this.rendererManager.mesh.get3d(n);
        } else if (isLightNode(n)) {
            return this.rendererManager.light.get3d(n);
        } else if (isSpaceNode(n)) {
            return this.rendererManager.mesh.get3d(n);
        } else if (isMaterialNode(n)) {
            return this.rendererManager.material.get3d(n);
        } else if (isConnector(n)) {
            return this.rendererManager.connector.get3d(n);
        } else if (isSketchControlPoint(n)) {
            return this.rendererManager.controlPointRenderer.get3d(n);
        } else if (isTextureLayer(n)) {
            return this.rendererManager.textures.get3d(n);
        }
        return undefined;
    }

    getFPS() {
        return this.engine.getFps().toFixed();
    }

    getWarnings() {
        return this.warningMessages;
    }

    getMeshBoundingBox(node: MeshNode, vertexGroups?: IVertexGroup[]) {
        let mesh = this.rendererManager.mesh.get3d(node);
        let newMesh: Mesh | undefined;
        if (vertexGroups) {
            const newIndices: number[] = [];
            const newPositions: number[] = [];
            let indices: number[];
            let positions: number[];
            if (mesh instanceof Mesh && mesh.geometry) {
                positions = mesh.geometry.getVerticesData(VertexBuffer.PositionKind) as number[];
                indices = mesh.geometry.getIndices() as number[];
            } else {
                indices = [];
                positions = [];
            }
            for (let i = 0; i < vertexGroups.length; i++) {
                const vertexGroup = vertexGroups[i];
                if (vertexGroup && vertexGroup.startIndex && vertexGroup.endIndex) {
                    for (let j = vertexGroup.startIndex; j < vertexGroup.endIndex; j++) {
                        newIndices.push(indices[j]);
                        for (let k = 0; k < 3; k++) {
                            newPositions[indices[j] * 3 + k] = positions[indices[j] * 3 + k];
                        }
                    }
                }
            }
            newMesh = new Mesh('temp mesh', this.scene, mesh?.parent);
            const newVertexData = new VertexData();
            newVertexData.indices = newIndices;
            newVertexData.positions = newPositions;
            newVertexData.applyToMesh(newMesh, true);
            newMesh.visibility = 0;
        }

        if (newMesh) {
            mesh = newMesh;
        }
        if (mesh && mesh instanceof AbstractMesh) {
            const boundingBox = mesh.getBoundingInfo().boundingBox;
            newMesh?.dispose();
            return {
                maximum: boundingBox.maximum,
                minimum: boundingBox.minimum,
                center: boundingBox.center,
                minWorld: boundingBox.minimumWorld,
                maxWorld: boundingBox.maximumWorld,
            };
        }
    }

    updateLightWarnings() {
        let totalSpotLights = 0;
        let totalPointLights = 0;
        let shadowCastingLights = 0;
        let tempWarning = '';
        this.lightsWarningMessages = [];

        this.scene.lights.forEach(light => {
            if (light.isEnabled()) {
                if (light instanceof SpotLight) {
                    totalSpotLights += 1;
                } else if (light instanceof PointLight) {
                    totalPointLights += 1;
                }
                if (light.shadowEnabled) {
                    shadowCastingLights += 1;
                }
            }
        });
        if (totalSpotLights + totalPointLights > 10) {
            tempWarning = `Performance Warning: The number of spotlights (${totalSpotLights}) and/or pointlights (${totalPointLights}) is higher than recommended for optimal performance.`;
            this.lightsWarningMessages.push(tempWarning);
            if (this.isDevEnvironment) {
                console.warn(tempWarning);
            }
        }
        if (shadowCastingLights > 4) {
            tempWarning = `Performance Warning: The number of shadow casting lights (${shadowCastingLights}) is higher than recommended for optimal performance.`;
            this.lightsWarningMessages.push(tempWarning);
            if (this.isDevEnvironment) {
                console.warn(tempWarning);
            }
        }
        this.updateWarnings();
    }

    updateTextureWarnings() {
        const bigTextures2048 = new Map<number, Texture>();
        const bigTextures4096 = new Map<number, Texture>();
        let tempWarning = '';
        this.textureWarningMessages = [];

        this.sceneNode.meshes.forEach(node => {
            const mesh = this.getRendererNode(node);
            let textures: Texture[] | BaseTexture[] = [];
            if (mesh instanceof AbstractMesh && mesh.isEnabled()) {
                if (mesh.material instanceof MultiMaterial) {
                    mesh.material.subMaterials.forEach(subMaterial => {
                        const subMaterialTextures = subMaterial?.getActiveTextures();
                        if (subMaterialTextures) {
                            subMaterialTextures.forEach(texture => {
                                if (texture instanceof Texture) {
                                    textures.push(texture);
                                }
                            });
                        }
                    });
                } else if (mesh.material instanceof Material) {
                    textures = mesh.material.getActiveTextures();
                }
            }

            if (textures) {
                textures.forEach(texture => {
                    const textureSize = Math.max(texture.getSize().height, texture.getSize().width);
                    if (texture instanceof Texture) {
                        if (textureSize > 4096) {
                            bigTextures4096.set(texture.uniqueId, texture);
                            bigTextures2048.set(texture.uniqueId, texture);
                        } else if (textureSize > 2048) {
                            bigTextures2048.set(texture.uniqueId, texture);
                        }
                    }
                });
            }
        });

        if (bigTextures4096.size > 4) {
            tempWarning = `Warning, number of textures with resolutions over 4096 (${bigTextures4096.size}) is higher than recommended for optimal performance.`;
            this.textureWarningMessages.push(tempWarning);
            if (this.isDevEnvironment) {
                console.warn(tempWarning);
            }
        } else if (bigTextures2048.size > 20) {
            tempWarning = `Warning, number of textures with resolutions over 2048 (${bigTextures2048.size}) is higher than recommended for optimal performance.`;
            this.textureWarningMessages.push(tempWarning);
            if (this.isDevEnvironment) {
                console.warn(tempWarning);
            }
        }
        this.updateWarnings();
    }

    updateMeshWarnings() {
        this.totalPolygons = this.recursiveGetPolygons(this.sceneNode).polygons;
        let meshCastAndReceiveShadows = 0;
        let tempWarning = '';
        this.meshWarningMessages = [];

        this.MeshWarningTracker.forEach(mesh => {
            if (mesh.isEnabled) {
                if (mesh.featureNumber > 15) {
                    tempWarning = `Performance Warning: The number of features on ${mesh.name} is higher than recommended for optimal performance.`;
                    this.meshWarningMessages.push(tempWarning);
                    if (this.isDevEnvironment) {
                        console.warn(tempWarning);
                    }
                }

                if (mesh.castShadows && mesh.receiveShadows) {
                    meshCastAndReceiveShadows += 1;
                }
            }
        });

        if (this.totalPolygons > 500000) {
            tempWarning = `Performance Warning: Polygon count of ${this.totalPolygons} is higher than recommended for optimal performance.`;
            this.meshWarningMessages.push(tempWarning);
            if (this.isDevEnvironment) {
                console.warn(tempWarning);
            }
        }

        if (meshCastAndReceiveShadows > 10) {
            tempWarning = `Performance Warning: The number of meshes that cast and receive shadows (${meshCastAndReceiveShadows}) is higher than recommended for optimal performance.`;
            this.meshWarningMessages.push(tempWarning);
            if (this.isDevEnvironment) {
                console.warn(tempWarning);
            }
        }

        this.updateWarnings();
    }

    updateFeatureWarnings(feature: Feature, polygonCount: number, removed?: boolean) {
        if (removed) {
            this.featureWarningMessages.delete(feature.id);
        } else {
            if (polygonCount > 100000 && feature.enabled) {
                const warning = `Performance Warning: Too many polygons (${polygonCount}) are selected inside "${feature.name}" on "${feature._parent?.name}".`;
                this.featureWarningMessages.set(feature.id, warning);
                if (this.isDevEnvironment) {
                    console.warn(warning);
                }
            } else {
                this.featureWarningMessages.delete(feature.id);
            }
        }
        this.updateWarnings();
    }

    updateMaterialWarnings() {
        let tempWarning = '';
        const localMaterialCount = this.getLocalMaterialsRecursive(this.sceneNode);
        this.materialWarningMessages = [];
        if (localMaterialCount > 20) {
            tempWarning = `Performance Warning: The number of local materials (${localMaterialCount}) is higher than recommended for optimal performance.`;
            this.materialWarningMessages.push(tempWarning);
            if (this.isDevEnvironment) {
                console.warn(tempWarning);
            }
        }
    }

    updateAnimationWarnings(activeAnimations: number) {
        let tempWarning = '';
        if (activeAnimations > 1) {
            tempWarning = `Performance Warning: More than 1 animation (${activeAnimations}) is active at the same instance.`;
            if (this.isDevEnvironment) {
                console.warn(tempWarning);
            }
        }
    }

    updateWarnings() {
        const tempArray: string[] = [];
        if (this.lightsWarningMessages && this.lightsWarningMessages.length > 0) {
            this.lightsWarningMessages.forEach(warning => {
                tempArray.push(warning);
            });
        }
        if (this.textureWarningMessages && this.textureWarningMessages.length > 0) {
            this.textureWarningMessages.forEach(warning => {
                tempArray.push(warning);
            });
        }
        if (this.meshWarningMessages && this.meshWarningMessages.length > 0) {
            this.meshWarningMessages.forEach(warning => {
                tempArray.push(warning);
            });
        }
        if (this.materialWarningMessages && this.materialWarningMessages.length > 0) {
            this.materialWarningMessages.forEach(warning => {
                tempArray.push(warning);
            });
        }
        if (this.featureWarningMessages && this.featureWarningMessages.size > 0) {
            this.featureWarningMessages.forEach(warning => {
                tempArray.push(warning);
            });
        }
        if (tempArray.length === 0) {
            tempArray.push('No warnings to display.');
        }
        this.warningMessages = tempArray;
        this.warningTracker.next();
    }

    extractMeshGeometry(node: MeshNode) {
        const mesh = this.rendererManager.mesh.get3d(node);
        if (mesh && mesh instanceof Mesh && mesh.geometry) {
            const newGeometry = new KbGeometry();
            newGeometry.indices = new Uint32Array(mesh.geometry.getIndices() || []);
            newGeometry.positions = new Float32Array(mesh.geometry.getVerticesData(VertexBuffer.PositionKind) || []);
            if (newGeometry.indices.length === 0 || newGeometry.positions.length === 0) {
                return null;
            }
            const normals = mesh.geometry.getVerticesData(VertexBuffer.NormalKind);
            if (normals) {
                newGeometry.normals = new Float32Array(normals);
            }
            const tangents = mesh.geometry.getVerticesData(VertexBuffer.TangentKind);
            if (tangents) {
                newGeometry.tangents = new Float32Array(tangents);
            }
            const uvs = mesh.geometry.getVerticesData(VertexBuffer.UVKind);
            if (uvs) {
                newGeometry.uvs = new Float32Array(uvs);
            }
            const colors = mesh.geometry.getVerticesData(VertexBuffer.ColorKind);
            if (colors) {
                newGeometry.colors = new Float32Array(colors);
            }

            return newGeometry;
        }
    }

    applySvgToSketch(node: SketchNode, data: Blob) {
        return SvgConverter.overwriteSketchWithSvg(node, data);
    }

    draggingEvent(mousePosition: Observable<{ x: number; y: number }>) {
        let lastPickedMesh: SpaceNode | undefined;
        const dragOver = new Subject<SpaceNode | undefined>();
        mousePosition
            .pipe(
                takeUntil(this.disposed),
                finalize(() => {
                    if (lastPickedMesh) {
                        this.highlighter.remove(lastPickedMesh);
                        dragOver.complete();
                    }
                })
            )
            .subscribe(position => {
                const pickInfo = this.interaction.pick(position.x, position.y);
                const mesh = pickInfo?.targetNode;
                if (lastPickedMesh !== mesh) {
                    if (lastPickedMesh) {
                        this.highlighter.remove(lastPickedMesh);
                    }
                    if (mesh) {
                        this.highlighter.add(mesh, KbColor.Green());
                    }
                    dragOver.next(mesh);
                    lastPickedMesh = mesh;
                }
            });
        return dragOver.pipe(takeUntil(this.disposed));
    }

    private setupPrintScreenListener() {
        if (typeof document !== 'undefined') {
            this.printScreenListener = (event: KeyboardEvent) => {
                // PrintScreen key has keyCode 44 or key 'PrintScreen'
                if (event.key === 'M' || event.keyCode === 77) {
                    event.preventDefault();
                    this.renderFullResolutionFrame();

                    setTimeout(() => {
                        this.endRenderFullResolutionFrame();
                    }, 1000);
                }
            };
            document.addEventListener('keydown', this.printScreenListener);
        }
    }

    dispose() {
        this.isDisposed = true;
        this.disposed.next();
        this.positionTrackedNodes.clear();
        this.rendererManager.disposeAll();
        this.scene.dispose();
        this.engine.dispose();
        this.camera!.dispose();
        this.manager.dispose();
        this.bbCache.dispose();
        this.sketchEditor.dispose();
        if (this.canvas) {
            this.container.removeChild(this.canvas);
            delete this.canvas;
        }
        if (this.uiOverlay) {
            this.container.removeChild(this.uiOverlay);
            delete this.uiOverlay;
        }
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            delete this.resizeObserver;
        }
        if (this.printScreenListener && typeof document !== 'undefined') {
            document.removeEventListener('keydown', this.printScreenListener);
            this.printScreenListener = undefined;
        }
        this.fpsTracker.complete();
        this.polygonTracker.complete();
        this.warningTracker.complete();
        this.meshWarningsNeedsUpdate.complete();
        this.materialWarningsNeedsUpdate.complete();
        this.animationWarningsNeedsUpdate.complete();
    }

    getScreenPositionOfNode(node: SpaceNode) {
        const bNode = this.rendererManager.mesh.get3d(node);

        if (bNode) {
            return nodeToScreen(bNode, this.scene.activeCamera!);
        }
    }

    getScreenBoundingBoxOfNode(node: SpaceNode) {
        const bNode = this.rendererManager.mesh.get3d(node);
        if (bNode) {
            return getScreenBoundingBox(bNode, this.scene.activeCamera!, true);
        }
    }

    getBabylonJson() {
        return JSON.stringify(SceneSerializer.Serialize(this.scene), null, 2);
    }

    exportGlb(sceneName?: string) {
        if (sceneName) {
            return this.gltfExporter.exportGlb(sceneName);
        } else {
            return this.gltfExporter.exportGlb(this.sceneNode?.name);
        }
    }

    exportGlbById(sceneId: string) {
        //get scene name by id here and plug it into the function under this
        return this.gltfExporter.exportGlb(this.sceneNode?.name);
    }

    exportObj(meshNode?: MeshNode) {
        const fileName = meshNode ? meshNode.name : this.sceneNode?.name;
        return this.objExporter.exportObj(fileName, meshNode);
    }

    //returns an object containing the polygons and vertices withing the selected node
    recursiveGetPolygons(node: SpaceNode) {
        if (node.visible == false) {
            return { polygons: 0, vertices: 0 };
        }
        if (node instanceof MeshNode) {
            return { polygons: node._polygonCount || 0, vertices: node._verticesCount || 0 };
        }
        const result = { polygons: 0, vertices: 0 };
        const children = node.getChildren();

        children.forEach(child => {
            if (child instanceof MeshNode || child instanceof MeshGroupNode) {
                const childData = this.recursiveGetPolygons(child);
                result.polygons += childData.polygons;
                result.vertices += childData.vertices;
            }
        });
        return result;
    }

    //call with null
    onNextFrame(func: () => void) {
        this.nextFrameCallbacks.push(func);
    }

    userIsInteracting() {
        return this.interaction.pointerIsDown || this.gizmos.dragging;
    }

    renderOne() {
        this.scene.render();
    }

    renderFullResolutionFrame() {
        this.freezeFrame = false;
        this.engine.setHardwareScalingLevel(1);
        return this.forceAndWaitForNextRender().then(() => {
            this.freezeFrame = true;
        });
    }

    endRenderFullResolutionFrame() {
        if (this.options.startInLowResolution) {
            this.engine.setHardwareScalingLevel(PRERENDER_SCALING_LEVEL);
        }
        this.freezeFrame = false;
    }

    /** runs the kb3d renderers.  Called before the babylon render */
    protected preRender() {
        // If this kb viewer instance is disposed, exit the render loop
        if (this.isDisposed) {
            return;
        }

        this.bbCache.update(); //update watchers of the bbcache of everything that changed since the last frame
        this.animation.update(); //update all animations.  This can result in changes, so should be called before the check for changes
        if (!this.rendererManager.isStable()) {
            /** nodes must be processed in a specific order.  For example, materials must be processed before meshes,
             *  or a new material wouldn't exist yet in babylon when the mesh renderer tries to apply it.
             */
            const hasChanges = SyncService.hasChanges;

            this.rendererManager.render();
            this.gizmos.updateGizmoPositions();

            //if there were changes, force a babylon render asap to keep responsive
            if (hasChanges) {
                this.forceAndWaitForNextRender();
            }

            this.viewChanged.next();

            while (this.nextFrameCallbacks.length > 0) {
                this.nextFrameCallbacks.pop()!();
            }
            nextTick();
        }
    }

    public startRender(): void {
        if (!this._renderLoopRunning) {
            this._renderLoopRunning = true;
            let frameCount = 0;
            // Run the render loop.
            this.engine.runRenderLoop(() => {
                this.preRender();
                let skipped = true;

                if (this.pauseRender) {
                    return;
                }
                frameCount++;
                if (
                    this.forceRunRenderer ||
                    this.forceRenderOnNextFrame ||
                    this.userIsInteracting() ||
                    this.camera!.cameraMoving ||
                    this.animation.animationsAreRunning ||
                    this.ar.xrActive
                ) {
                    if (!this.freezeFrame) {
                        this.scene.render();
                        skipped = false;
                    }
                    this.viewChanged.next();
                } else if (frameCount > 10) {
                    if (!this.freezeFrame) {
                        this.scene.render();
                        skipped = false;
                    }
                    this.fpsTracker.next(this.getFPS());
                }
                if (frameCount > 10) {
                    frameCount = 0;
                }
                this.afterRender.next({ skipped });
            });
        }
    }

    public stopRender() {
        this.engine.stopRenderLoop();
        this._renderLoopRunning = false;
    }

    protected createCanvas() {
        const canvas = document.createElement('canvas') as HTMLCanvasElement & Element;
        canvas.style.width = '100%';
        canvas.style.height = '100%';
        canvas.style.outline = 'none';
        canvas.style.opacity = '0';
        canvas.style.transition = `opacity ${FADE_TIME}ms linear`;
        this.container.appendChild(canvas);

        const spinnerStyle = document.createElement('style');
        spinnerStyle.innerText = `
.kb3d-feature-processing-indicator {
    width: 24px;
    height: 24px;
    border: 3px solid #FFF;
    border-bottom-color: transparent;
    border-radius: 50%;
    display: inline-block;
    box-sizing: border-box;
    animation: kb3d-feature-processing-indicator-rotation 1s linear infinite;
    position: absolute;
    bottom: 25px;
    left: 25px;
    display: none;
}
@keyframes kb3d-feature-processing-indicator-rotation {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}`;
        this.container.append(spinnerStyle);
        const loader = document.createElement('div');
        loader.className = 'kb3d-feature-processing-indicator';
        this.container.appendChild(loader);
        this.processingIndicator = loader;

        if (!this.container.style.position) {
            this.container.style.position = 'relative';
        }
        this.uiOverlay = this.createOverlay();
        this.container.appendChild(this.uiOverlay);

        const resizeSubject = new Subject<void>();
        this.resizeObserver = new ResizeObserver((entries) => {
            resizeSubject.next()
        });
        this.resizeObserver.observe(canvas);

        resizeSubject.pipe(throttleTime(1000), takeUntil(this.disposed)).subscribe(() => this.resize());

        return canvas;
        
    }

    protected createOverlay() {
        const overlay = document.createElement('div');
        overlay.style.position = 'absolute';
        overlay.style.top = '0';
        overlay.style.left = '0';
        overlay.style.width = '100%';
        overlay.style.height = '100%';
        overlay.style.pointerEvents = 'none';
        return overlay;
    }

    protected createLoadingExperience(loadingExperience: eLoadingExperience, loadingImagePath: string) {
        if (loadingExperience == eLoadingExperience.image) {
            const matService = this.manager.getService(Token.MaterialService)!;
            this.loadingOverlay = this.createOverlay();
            this.loadingOverlay.style.opacity = '0';
            this.loadingOverlay.style.transition = `opacity ${FADE_TIME}ms linear`;
            const img = document.createElement('img');
            img.style.position = 'absolute';
            img.style.width = '100%';
            img.style.height = '100%';
            img.style.backgroundSize = 'auto 100%';
            img.style.backgroundRepeat = 'no-repeat';
            img.style.backgroundPosition = 'center';
            img.style.backgroundColor = 'transparent';
            img.style.backgroundImage = `url('${matService.getAbsoluteTexturePath(loadingImagePath)}')`;
            this.loadingOverlay.appendChild(img);
            this.container.appendChild(this.loadingOverlay);
            this.loadingOverlay.style.opacity = '1';
        } else if (loadingExperience == eLoadingExperience.fade) {
            // do nothing
        } else if (loadingExperience == eLoadingExperience.spinner) {
            this.loadingOverlay = this.createOverlay();
            this.loadingOverlay.style.opacity = '0';
            this.loadingOverlay.style.transition = `opacity ${FADE_TIME}ms linear`;
            this.loadingOverlay.style.display = 'flex';
            this.loadingOverlay.style.alignItems = 'center';
            this.loadingOverlay.style.justifyContent = 'center';

            this.loadingOverlay.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" class="kb-svg-spinner" viewBox="0 0 50 50">
                <circle cx="50%" cy="50%" r="20" fill="none"  />
                <circle cx="50%" cy="50%" r="20" fill="none"  />
                <circle cx="50%" cy="50%" r="20" fill="none"  />
            </svg>`;
            this.container.appendChild(this.loadingOverlay);
            this.loadingOverlay.style.opacity = '1';
        } else {
            this.canvas!.style.opacity = '1';
        }
    }

    protected hideLoadingExperience() {
        if (this.loadingOverlay) {
            this.loadingOverlay.style.opacity = '0';

            setTimeout(() => {
                this.loadingOverlay?.remove();
                this.loadingOverlay = undefined;
            }, FADE_TIME);
        }
        this.canvas!.style.opacity = '1';
    }

    resize() {
        if (!this.ar || (!this.ar.xrLoading && !this.ar.xrActive)) {
            //don't mess with the AR canvas resize
            this.engine.resize();
            this.fireViewChangedOnNextRender();
        }
    }

    protected optimizeScene(scene: Scene) {
        if (this.sceneNode.enableSceneOptimization) {
            this.forceRunRenderer = true; // Run renderer until optimizer is done optimizing
            const sceneOptimizerOptions = new SceneOptimizerOptions(30, 2000);

            let priority = 0;
            sceneOptimizerOptions.addCustomOptimization(
                function () {
                    console.warn('Low scene performance detected - disabling 3d features to improve performance.');
                    return true;
                },
                function () {
                    return 'custom optimization to throw console warning when optimizer needs to run.';
                },
                priority
            );

            sceneOptimizerOptions.optimizations.push(new ShadowsOptimization(priority));
            sceneOptimizerOptions.optimizations.push(new LensFlaresOptimization(priority));

            // Next priority
            priority++;
            // sceneOptimizerOptions.optimizations.push(new PostProcessesOptimization(priority));
            sceneOptimizerOptions.optimizations.push(new ParticlesOptimization(priority));

            // priority++;
            // sceneOptimizerOptions.optimizations.push(new TextureOptimization(priority, 256));

            // priority++;
            // sceneOptimizerOptions.optimizations.push(new RenderTargetsOptimization(priority));

            // Don't scale the resolution when in editor mode because it affects usability
            if (!this.options.editorMode) {
                priority++;
                sceneOptimizerOptions.optimizations.push(new HardwareScalingOptimization(priority, 4));
            }

            const sceneOptimizer = new SceneOptimizer(scene, sceneOptimizerOptions, false);
            sceneOptimizer.onSuccessObservable.add(() => {
                this.startSceneRenderAutoStop(scene, this.engine);

                // Run optimizer in improvement mode in case the scene speeds up later
                // new SceneOptimizer(scene, sceneOptimizerOptions, false, true);
            });
            sceneOptimizer.onFailureObservable.add(() => this.startSceneRenderAutoStop(scene, this.engine));

            sceneOptimizer.start();
        }
    }

    protected createScene() {
        // Create a basic BJS Scene object.
        const scene = new Scene(this.engine);
        scene.useRightHandedSystem = true;
        scene.clearColor = new Color4(0, 0, 0, 0); //default background to transparent

        // Create a FreeCamera, and set its position to (x:0, y:5, z:-10).
        //this.m_camera = new FreeCamera('camera1', new Vector3(0, 5, -10), this.m_scene);

        //scene.createDefaultCameraOrLight(true, true, true);

        this.createDefaultEnv();

        return scene;
    }

    protected startSceneRenderAutoStop(scene: Scene, engine: Engine) {
        this.forceRunRenderer = false;
    }

    protected createDefaultEnv() {
        if (!this.scene) {
            return;
        }
        const helper = this.scene.createDefaultEnvironment({
            skyboxSize: 500,
            groundShadowLevel: 0.5,
        });

        if (helper !== null && helper.skybox !== null && helper.ground !== null) {
            helper.setMainColor(Color3.Gray());
            helper.rootMesh.position.y = -5;
            helper.skybox.isPickable = false;
            helper.rootMesh.isPickable = false;
            helper.ground.isPickable = false;
            //helper.rootMesh.name = KbViewer.KBMAXPREFIX + helper.rootMesh.name;
            //helper.skybox.name = KbViewer.KBMAXPREFIX + helper.skybox.name;
            for (let i = 0; i < this.scene.meshes.length; i++) {
                this.scene.meshes[i].name = KBMAXPREFIX + this.scene.meshes[i].name;
                this.scene.meshes[i].id = KBMAXPREFIX + this.scene.meshes[i].id;
            }
        }
    }

    public pauseRenderWhile(action: () => void) {
        this.pauseRender = true;
        action();
        this.pauseRender = false;
    }

    /**
     * returns a promise that is resolved when the following has happened:
     * * the scene graph is fully loaded
     * * at least one pass through the render loop since the scene has been loaded has completed
     * * any server assets in flight (meshes, textures) have been received and processed
     */
    public whenAssetsLoaded(waitForMaterials = true) {
        if (!this.sceneNode) {
            throw Error('a scene needs to be loaded into the viewer to wait for assets');
        }

        const promise = new Promise<void>((resolve, reject) => {
            const http = this.sceneNode._manager?.getService(Token.HttpService);
            const materialService = this.sceneNode._manager?.getService(Token.MaterialService);

            if (!http) {
                throw Error('HTTP API service could not be initialized');
            }
            if (!materialService) {
                throw Error('Material service could not be initialized');
            }

            this.forceAndWaitForNextRender().then(() => {
                const promises = [
                    ...http.trackedAssetCalls,
                    ...this.additionalLoadBlockers,
                    this.scene.whenReadyAsync(),
                ];
                if (waitForMaterials) {
                    promises.push(...materialService.trackedAssetCalls);
                }
                Promise.all(promises).finally(() => {
                    // now wait for a render loop and make sure no new loading blockers were added since the last batch
                    this.forceAndWaitForNextRender().then(() => {
                        if (
                            http.trackedAssetCalls.length ||
                            this.additionalLoadBlockers.length ||
                            (waitForMaterials && materialService.trackedAssetCalls.length) ||
                            !this.scene.isReady() ||
                            !this.rendererManager.isStable()
                        ) {
                            return this.whenAssetsLoaded(waitForMaterials).finally(() => {
                                resolve();
                            });
                        } else {
                            resolve();
                        }
                    });
                });
            });
        });

        return promise;
    }

    public deferSceneReady(promise: Promise<any>) {
        promise.then(() => {
            this.additionalLoadBlockers = this.additionalLoadBlockers.filter(somePromise => somePromise !== promise);
        });

        this.additionalLoadBlockers.push(promise);
    }

    /**
     * Returns a promise that resolves when the next kb3d render processing has happened.
     * Note that this does not necessarily mean that the babylon render has happened!
     * If you want to wait for both, see forceAndWaitForNextRender
     */
    protected waitForNextKb3dRender() {
        return firstValueFrom(this.afterRender.pipe(filter(args => !args.skipped), take(1)));
    }

    public forceAndWaitForNextRender() {
        this.forceRenderOnNextFrame = true;
        return this.waitForNextKb3dRender().then(() => {
            return new Promise<void>((resolve) => {
                this.scene.onAfterRenderObservable.addOnce(() => {
                    this.forceRenderOnNextFrame = false;
                    resolve();
                });
            })
        });
    }

    public async waitForStableRender(timeout = 5000) {
        const start = performance.now();

        await this.forceAndWaitForNextRender();
        while (!this.rendererManager.isStable()) {
            await this.forceAndWaitForNextRender();

            if (performance.now() - start > timeout) {
                console.error(`waiting for stable render took longer than timeout of ${timeout}ms, unblocking loading`);
                return false;
            }
        }

        return true;
    }

    public fireViewChangedOnNextRender() {
        this.forceAndWaitForNextRender().then(() => {
            this.viewChanged.next();
        });
    }

    public getActiveMates() {
        const mates = new Array<MateNode>();

        this.sceneNode.runOnAllScenes(s => {
            mates.push(...s._manager!.getByToken(Token.MateNode).filter(m => m.isActive()));
        });

        return mates;
    }

    public nodesIntersect(node1: SpaceNode, node2: SpaceNode) {
        const bb1 = this.bbCache.getWorld(node1);
        const bb2 = this.bbCache.getWorld(node2);
        if (bb1 && bb2) {
            return bb1.boundingInfo.intersects(bb2.boundingInfo as DeepImmutableObject<BoundingInfo>, false);
        }
        return false;
    }

    public getReferencesToNode(node: Kb3dObject) {
        const result: Kb3dObject[] = [];
        const referencers = this.rendererManager.referenceProcessor.getReferencesTo(node);
        if (referencers) {
            const referencersInfo = referencers.values();
            for (const { ref } of referencersInfo) {
                result.push(ref);
            }
            return result;
        } else {
            return [];
        }
    }

    public getLocalMaterialsRecursive(node: SceneNode) {
        let result = 0;

        if (!node.scenes) {
            return node.materials.length | 0;
        } else {
            node.scenes.forEach(scene => {
                result += this.getLocalMaterialsRecursive(scene);
            });
            result += node.materials.length;
        }
        return result;
    }

    public getTransformSpaceDifference(originNode: Kb3dObject, destinationNode: Kb3dObject) {
        const originMesh = this.getRendererNode(originNode as SpaceNode);
        const destinationMesh = this.getRendererNode(destinationNode as SpaceNode);
        const result = {
            scale: Vector3.One(),
            rotationQuaternion: new Quaternion(),
            translation: Vector3.Zero(),
        };
        if (!destinationMesh) {
            const resultMatrix = originMesh.getWorldMatrix();
            resultMatrix.decompose(result.scale, result.rotationQuaternion, result.translation);
        } else if (originMesh && destinationMesh) {
            const originMatrix = originMesh.getWorldMatrix();
            const destinationMatrix = destinationMesh.getWorldMatrix().clone();
            destinationMatrix.invert();
            const resultMatrix = originMatrix.multiply(destinationMatrix);
            resultMatrix.decompose(result.scale, result.rotationQuaternion, result.translation);
        } else {
            return null;
        }
        return {
            scale: result.scale.x,
            rotationQuaternion: KbQuaternion.FromQuat(result.rotationQuaternion),
            translation: KbVector.FromVec3(result.translation),
        };
    }

    public centerMeshToOrigin(node: Kb3dObject) {
        const boundingBox = this.getMeshBoundingBox(node as MeshNode);
        const center = boundingBox?.maxWorld.add(boundingBox.minWorld);
        if (center) {
            center?.scaleInPlace(-0.5);
            const result = new KbVector(center.x, center.y, center.z);
            return result;
        } else {
            return new KbVector(0, 0, 0);
        }
    }
}
